import { zodResolver } from '@hookform/resolvers/zod';
import { emailSchema, passwordSchema } from '@serviceportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, UserPlus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';
import { AuthLayout, usePublicBranding } from '@/components/layout/auth-layout';
import { TwoFactorCard } from '@/components/two-factor';
import { Button } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field, PageHeader } from '@/components/ui/form-field';
import { Alert, Badge, Card, CardContent, CardFooter, CardHeader, CardTitle, Checkbox, Input, Select } from '@/components/ui/primitives';
import { EmptyRow, Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { api, errorMessage } from '@/lib/api';
import { ME_KEY } from '@/lib/auth';
import type { CompanyUser } from '@/lib/types';
import { PasswordCard } from '../profile-page';
import { usePortalMe } from './portal-shell';

export function PortalProfilePage() {
  const qc = useQueryClient();
  const { data: me } = usePortalMe();
  const [f, setF] = useState({ firstName: '', lastName: '', phone: '', mobile: '', street: '', postalCode: '', city: '', customerPhone: '' });
  useEffect(() => {
    if (!me) return;
    setF({
      firstName: me.contact.firstName,
      lastName: me.contact.lastName,
      phone: me.contact.phone ?? '',
      mobile: me.contact.mobile ?? '',
      street: me.customer.street ?? '',
      postalCode: me.customer.postalCode ?? '',
      city: me.customer.city ?? '',
      customerPhone: me.customer.phone ?? '',
    });
  }, [me]);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  const save = useMutation({
    mutationFn: () =>
      api('/portal/profile', {
        method: 'PUT',
        body: {
          firstName: f.firstName,
          lastName: f.lastName,
          phone: f.phone || null,
          mobile: f.mobile || null,
          customer: me?.canEditCustomer ? { street: f.street, postalCode: f.postalCode, city: f.city, phone: f.customerPhone } : undefined,
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portal', 'me'] });
      qc.invalidateQueries({ queryKey: ME_KEY });
      toast.success('Ihre Daten wurden gespeichert');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  if (!me) return null;
  const business = me.customer.type === 'BUSINESS';

  return (
    <>
      <PageHeader title="Meine Daten" description={`${me.customer.name} · Kundennummer ${me.customer.number}`} />
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate();
            }}
          >
            <CardHeader>
              <CardTitle>Kontaktdaten</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <Field label="Vorname" htmlFor="pp-first">
                <Input id="pp-first" value={f.firstName} onChange={set('firstName')} />
              </Field>
              <Field label="Nachname" htmlFor="pp-last">
                <Input id="pp-last" value={f.lastName} onChange={set('lastName')} />
              </Field>
              <Field label="Telefon" htmlFor="pp-phone">
                <Input id="pp-phone" value={f.phone} onChange={set('phone')} />
              </Field>
              <Field label="Mobil" htmlFor="pp-mobile">
                <Input id="pp-mobile" value={f.mobile} onChange={set('mobile')} />
              </Field>
              <p className="text-xs text-muted-foreground sm:col-span-2">E-Mail-Adresse: {me.contact.email} (Änderung bitte über unser Service-Team)</p>
              {me.canEditCustomer && (
                <>
                  <p className="pt-2 text-sm font-medium sm:col-span-2">{business ? 'Firmenanschrift' : 'Anschrift'}</p>
                  <Field label="Straße und Hausnummer" htmlFor="pp-street" className="sm:col-span-2">
                    <Input id="pp-street" value={f.street} onChange={set('street')} />
                  </Field>
                  <Field label="PLZ" htmlFor="pp-zip">
                    <Input id="pp-zip" value={f.postalCode} onChange={set('postalCode')} />
                  </Field>
                  <Field label="Ort" htmlFor="pp-city">
                    <Input id="pp-city" value={f.city} onChange={set('city')} />
                  </Field>
                  {business && (
                    <Field label="Telefon (Zentrale)" htmlFor="pp-cphone">
                      <Input id="pp-cphone" value={f.customerPhone} onChange={set('customerPhone')} />
                    </Field>
                  )}
                </>
              )}
            </CardContent>
            <CardFooter>
              <Button type="submit" loading={save.isPending} disabled={!f.firstName.trim() || !f.lastName.trim()}>
                Speichern
              </Button>
            </CardFooter>
          </form>
        </Card>
        <PasswordCard />
        <TwoFactorCard />
      </div>
    </>
  );
}

const inviteSchema = z.object({ firstName: z.string().trim().min(1, 'Pflichtfeld'), lastName: z.string().trim().min(1, 'Pflichtfeld'), email: emailSchema });

export function PortalCompanyPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const { data } = useQuery({ queryKey: ['portal', 'company'], queryFn: () => api<CompanyUser[]>('/portal/company/users') });
  const form = useForm<z.infer<typeof inviteSchema>>({ resolver: zodResolver(inviteSchema), defaultValues: { firstName: '', lastName: '', email: '' } });
  const invite = useMutation({
    mutationFn: (v: z.infer<typeof inviteSchema>) => api('/portal/company/users', { method: 'POST', body: { ...v, isAdmin } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portal', 'company'] });
      toast.success('Einladung versendet');
      form.reset();
      setIsAdmin(false);
      setOpen(false);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const toggle = useMutation({
    mutationFn: ({ contactId, active }: { contactId: string; active: boolean }) => api(`/portal/company/users/${contactId}`, { method: 'PATCH', body: { active } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['portal', 'company'] }),
    onError: (e) => toast.error(errorMessage(e)),
  });
  const STATUS = { NONE: 'kein Zugang', INVITED: 'eingeladen', ACTIVE: 'aktiv', DISABLED: 'gesperrt' } as const;

  return (
    <>
      <PageHeader
        title="Benutzer meiner Firma"
        description="Kollegen erhalten Zugang zum Portal und sehen ihre eigenen Anfragen; Administratoren sehen alle Anfragen der Firma."
        actions={
          <Button onClick={() => setOpen(true)}>
            <UserPlus /> Kollegen einladen
          </Button>
        }
      />
      <Card>
        <Table>
          <THead>
            <TR className="hover:bg-transparent">
              <TH>Name</TH>
              <TH className="hidden sm:table-cell">E-Mail</TH>
              <TH>Zugang</TH>
              <TH className="w-28" />
            </TR>
          </THead>
          <TBody>
            {data?.length === 0 && <EmptyRow colSpan={4}>Keine Benutzer.</EmptyRow>}
            {data?.map((u) => (
              <TR key={u.contactId}>
                <TD className="font-medium">
                  {u.firstName} {u.lastName} {u.isAdmin && <Badge variant="secondary">Administrator</Badge>} {u.isSelf && <Badge variant="outline">Sie</Badge>}
                </TD>
                <TD className="hidden text-muted-foreground sm:table-cell">{u.email ?? '–'}</TD>
                <TD>{STATUS[u.status]}</TD>
                <TD className="text-right">
                  {!u.isSelf && u.status !== 'NONE' && (
                    <Button size="sm" variant="outline" loading={toggle.isPending && toggle.variables?.contactId === u.contactId} onClick={() => toggle.mutate({ contactId: u.contactId, active: u.status === 'DISABLED' })}>
                      {u.status === 'DISABLED' ? 'Freigeben' : 'Sperren'}
                    </Button>
                  )}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <form onSubmit={form.handleSubmit((v) => invite.mutate(v))} noValidate className="flex min-h-0 flex-col">
            <DialogHeader>
              <DialogTitle>Kollegen einladen</DialogTitle>
            </DialogHeader>
            <DialogBody className="grid gap-4 sm:grid-cols-2">
              <Field label="Vorname" htmlFor="i-first" error={form.formState.errors.firstName?.message}>
                <Input id="i-first" {...form.register('firstName')} />
              </Field>
              <Field label="Nachname" htmlFor="i-last" error={form.formState.errors.lastName?.message}>
                <Input id="i-last" {...form.register('lastName')} />
              </Field>
              <Field label="E-Mail-Adresse" htmlFor="i-email" error={form.formState.errors.email?.message} className="sm:col-span-2">
                <Input id="i-email" type="email" {...form.register('email')} />
              </Field>
              <label className="flex items-center gap-2 text-sm sm:col-span-2">
                <Checkbox checked={isAdmin} onCheckedChange={(v) => setIsAdmin(v === true)} />
                Administrator (sieht alle Anfragen der Firma und verwaltet Benutzer)
              </label>
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Abbrechen
              </Button>
              <Button type="submit" loading={invite.isPending}>
                Einladen
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

const registerSchema = z
  .object({
    type: z.enum(['PRIVATE', 'BUSINESS']),
    companyName: z.string().trim().max(200),
    firstName: z.string().trim().min(1, 'Pflichtfeld').max(100),
    lastName: z.string().trim().min(1, 'Pflichtfeld').max(100),
    email: emailSchema,
    phone: z.string().trim().max(50),
    password: passwordSchema,
    confirm: z.string(),
    acceptPrivacy: z.boolean().refine((v) => v, 'Bitte bestätigen'),
    website: z.string().max(0),
  })
  .refine((v) => v.password === v.confirm, { path: ['confirm'], message: 'Die Passwörter stimmen nicht überein' })
  .refine((v) => v.type === 'PRIVATE' || v.companyName.length >= 2, { path: ['companyName'], message: 'Firmenname erforderlich' });

export function RegisterPage() {
  const { data: branding } = usePublicBranding();
  const form = useForm<z.infer<typeof registerSchema>>({
    resolver: zodResolver(registerSchema),
    defaultValues: { type: 'PRIVATE', companyName: '', firstName: '', lastName: '', email: '', phone: '', password: '', confirm: '', acceptPrivacy: false, website: '' },
  });
  const register = useMutation({
    mutationFn: ({ confirm: _c, ...v }: z.infer<typeof registerSchema>) =>
      api('/portal/register', { method: 'POST', body: { ...v, companyName: v.companyName || undefined, phone: v.phone || undefined, website: v.website || undefined } }),
  });
  const err = form.formState.errors;
  const type = form.watch('type');

  if (branding && !branding.portalSelfRegistration) {
    return (
      <AuthLayout title="Registrierung">
        <Alert>Eine Selbstregistrierung ist für dieses Portal nicht vorgesehen. Bitte wenden Sie sich an unser Service-Team.</Alert>
      </AuthLayout>
    );
  }
  if (register.isSuccess) {
    return (
      <AuthLayout title="Fast geschafft">
        <div className="flex flex-col items-center gap-3 text-center">
          <CheckCircle2 className="size-10 text-success" />
          <p className="text-sm">Bitte bestätigen Sie Ihre E-Mail-Adresse über den Link, den wir Ihnen gerade gesendet haben. Danach können Sie sich anmelden.</p>
          <Button asChild variant="outline" className="w-full">
            <Link to="/login">Zur Anmeldung</Link>
          </Button>
        </div>
      </AuthLayout>
    );
  }
  return (
    <AuthLayout title="Kundenkonto erstellen" description="Verfolgen Sie Reparaturen und Anfragen online.">
      <form className="flex flex-col gap-3" onSubmit={form.handleSubmit((v) => register.mutate(v))} noValidate>
        {register.isError && <Alert variant="destructive">{errorMessage(register.error)}</Alert>}
        <Field label="Ich bin" htmlFor="r-type">
          <Select id="r-type" {...form.register('type')}>
            <option value="PRIVATE">Privatkunde</option>
            <option value="BUSINESS">Geschäftskunde</option>
          </Select>
        </Field>
        {type === 'BUSINESS' && (
          <Field label="Firma" htmlFor="r-company" error={err.companyName?.message}>
            <Input id="r-company" {...form.register('companyName')} />
          </Field>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Vorname" htmlFor="r-first" error={err.firstName?.message}>
            <Input id="r-first" autoComplete="given-name" {...form.register('firstName')} />
          </Field>
          <Field label="Nachname" htmlFor="r-last" error={err.lastName?.message}>
            <Input id="r-last" autoComplete="family-name" {...form.register('lastName')} />
          </Field>
        </div>
        <Field label="E-Mail-Adresse" htmlFor="r-email" error={err.email?.message}>
          <Input id="r-email" type="email" autoComplete="email" {...form.register('email')} />
        </Field>
        <Field label="Telefon (optional)" htmlFor="r-phone">
          <Input id="r-phone" type="tel" autoComplete="tel" {...form.register('phone')} />
        </Field>
        <Field label="Passwort" htmlFor="r-pw" error={err.password?.message}>
          <Input id="r-pw" type="password" autoComplete="new-password" {...form.register('password')} />
        </Field>
        <Field label="Passwort wiederholen" htmlFor="r-pw2" error={err.confirm?.message}>
          <Input id="r-pw2" type="password" autoComplete="new-password" {...form.register('confirm')} />
        </Field>
        {/* Honeypot gegen Bots – für Menschen unsichtbar */}
        <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 opacity-0" {...form.register('website')} />
        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" className="mt-1 accent-[var(--primary)]" {...form.register('acceptPrivacy')} />
          <span>Ich habe die Datenschutzhinweise gelesen und bin mit der Verarbeitung meiner Daten zur Bearbeitung meiner Anfragen einverstanden.</span>
        </label>
        {err.acceptPrivacy && <p className="text-xs text-destructive">{err.acceptPrivacy.message}</p>}
        <Button type="submit" loading={register.isPending} className="mt-1">
          Konto erstellen
        </Button>
        <Link to="/login" className="text-center text-sm text-muted-foreground hover:text-foreground">
          Bereits registriert? Anmelden
        </Link>
      </form>
    </AuthLayout>
  );
}
